import React, { useEffect, useState } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import DoctorFooter from "./DoctorFooter";
import { Button, IconButton } from "@mui/material";
import {
  ArrowUp,
  AlertTriangle,
  Menu,
  Wallet,
  Clock3,
  TrendingUp,
  ShieldAlert,
} from "lucide-react";
import axios from "axios";

const DoctorEarningsPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [balance, setBalance] = useState(0);

  const [history, setHistory] = useState([
    {
      id: 1,
      amount: 5000,
      date: "2026-02-20",
      status: "Completed",
      dispute: false,
    },
    {
      id: 2,
      amount: 15000,
      date: "2026-02-18",
      status: "Completed",
      dispute: false,
    },
    {
      id: 3,
      amount: 8000,
      date: "2026-02-15",
      status: "Pending",
      dispute: false,
    },
  ]);

  const handleWithdraw = () => {
    const amount = Number(withdrawAmount);

    if (!amount || amount <= 0) return alert("Enter a valid amount");
    if (amount > balance) return alert("Amount exceeds balance");

    setBalance(balance - amount);

    setHistory([
      {
        id: history.length + 1,
        amount,
        date: new Date().toLocaleDateString(),
        status: "Pending",
        dispute: false,
      },
      ...history,
    ]);

    setWithdrawAmount("");
    setWithdrawOpen(false);
  };

  const raiseDispute = (id) => {
    setHistory((prev) =>
      prev.map((h) => (h.id === id ? { ...h, dispute: true } : h))
    );
  };

  const getWalletBalance = async () => {
    try {
      const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + `/doctor-wallet`,
        {
          doctorId: doctor?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setBalance(res.data.wallet || 0);
    } catch (err) {
      console.error("Failed to fetch wallet balance", err);
    }
  };

  useEffect(() => {
    getWalletBalance();
  }, []);

  const pendingWithdrawals = history.filter(
    (h) => h.status === "Pending"
  ).length;

  const completedWithdrawals = history.filter(
    (h) => h.status === "Completed"
  ).length;

  const formatMoney = (amount) => `₦${Number(amount || 0).toLocaleString()}`;

  const stats = [
    {
      title: "Wallet balance",
      value: formatMoney(balance),
      note: "Current available balance",
      icon: Wallet,
    },
    {
      title: "This month",
      value: formatMoney(balance),
      note: "Estimated earnings",
      icon: TrendingUp,
    },
    {
      title: "Pending",
      value: pendingWithdrawals,
      note: "Pending withdrawals",
      icon: Clock3,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Dashboard</p>
          <h1 className="text-base font-semibold text-slate-950">Earnings</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Earnings" setActiveTab={() => {}} />
        </div>

        {/* Mobile Drawer */}
        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Earnings"
          setActiveTab={() => setDrawerOpen(false)}
        />

        {/* Main */}
        <main className="relative flex-1 overflow-y-auto">
          {/* Coming soon overlay */}
          <div className="absolute inset-0 z-50 flex items-start justify-center bg-slate-50/70 px-4 pt-24 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                <ShieldAlert size={22} />
              </div>

              <h2 className="mt-5 text-xl font-semibold tracking-[-0.03em] text-slate-950">
                Earnings dashboard
              </h2>

              <p className="mt-2 text-sm font-medium text-slate-500">
                Coming soon
              </p>

              <p className="mt-4 text-sm leading-6 text-slate-500">
                We are building this carefully to support transparent doctor
                earnings, withdrawals, and payment history.
              </p>
            </div>
          </div>

          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page title */}
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                  Earnings
                </h2>

                <p className="mt-2 text-sm text-slate-500">
                  Track wallet balance, withdrawals, and payout history.
                </p>
              </div>

              <Button
                startIcon={<ArrowUp size={16} />}
                onClick={() => setWithdrawOpen(true)}
                sx={{
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1.2,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#064b43",
                    boxShadow: "none",
                  },
                }}
              >
                Withdraw
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {stats.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-slate-200 bg-white p-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{item.title}</p>

                        <p className="mt-3 text-2xl font-semibold tracking-[-0.03em] text-slate-950">
                          {item.value}
                        </p>

                        <p className="mt-2 text-xs text-slate-400">
                          {item.note}
                        </p>
                      </div>

                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                        <Icon size={18} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Secondary overview */}
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-slate-950">
                      Earnings analytics
                    </h3>

                    <p className="mt-1 text-sm text-slate-500">
                      Weekly and monthly trends.
                    </p>
                  </div>

                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                    Coming soon
                  </span>
                </div>

                <div className="mt-8 flex h-28 items-end gap-2">
                  {[35, 60, 48, 82, 50, 75, 42].map((height, index) => (
                    <div
                      key={index}
                      className="w-full rounded-t-lg bg-slate-200"
                      style={{ height: `${height}%` }}
                    />
                  ))}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-slate-950">
                      Payout insights
                    </h3>

                    <p className="mt-1 text-sm text-slate-500">
                      Withdrawal and dispute overview.
                    </p>
                  </div>

                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                    Coming soon
                  </span>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <span className="text-sm text-slate-500">
                      Completed withdrawals
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      {completedWithdrawals}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <span className="text-sm text-slate-500">
                      Pending withdrawals
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      {pendingWithdrawals}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">
                      Disputes raised
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      {history.filter((h) => h.dispute).length}
                    </span>
                  </div>
                </div>
              </section>
            </div>

            {/* History */}
            <section className="mt-6 rounded-2xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-5 py-4">
                <h3 className="text-base font-semibold text-slate-950">
                  Withdrawal history
                </h3>

                <p className="mt-1 text-sm text-slate-500">
                  Recent withdrawal requests and payout status.
                </p>
              </div>

              <div className="divide-y divide-slate-100">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col gap-4 px-5 py-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="text-base font-semibold text-slate-950">
                        {formatMoney(item.amount)}
                      </p>

                      <p className="mt-1 text-sm text-slate-500">
                        {item.date}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          item.status === "Completed"
                            ? "bg-emerald-50 text-emerald-700"
                            : item.status === "Pending"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {item.status}
                      </span>

                      {item.dispute ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                          <AlertTriangle size={14} />
                          Disputed
                        </span>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => raiseDispute(item.id)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "10px",
                            borderColor: "#e2e8f0",
                            color: "#334155",
                            fontWeight: 500,
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "#cbd5e1",
                              bgcolor: "#f8fafc",
                            },
                          }}
                        >
                          Raise dispute
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>

    </div>
  );
};

export default DoctorEarningsPage;
